import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { STRAINS_LIST } from '../../game/data/strains';
import { LICENSE_NAMES } from '../../game/systems/economics';
import { staggerContainer, staggerChild, buttonHover, buttonTap } from '../../ui/animations';

const CHANNEL_LABELS = { wholesale: '📦', medical: '🏥', retail: '🏪', blackmarket: '🚫' };
const RARITY_COLORS_GEN = { common: 'var(--green)', uncommon: 'var(--blue)', rare: '#d29922' };
const RARITY_LABELS_GEN  = { common: 'Common', uncommon: 'Uncommon', rare: 'Rare ✨' };

function PhenoEntry({ pheno }) {
  const bonusList = Object.entries(pheno.bonuses)
    .map(([stat, val]) => `+${Math.round(val * 100)}% ${stat}`)
    .join(', ');
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 8px',
      background: 'var(--border-light)',
      borderRadius: 'var(--radius-sm)',
      marginBottom: 4,
    }}>
      <span style={{
        fontSize: 10,
        fontWeight: 700,
        color: RARITY_COLORS_GEN[pheno.rarity],
        width: 66,
        flexShrink: 0,
      }}>
        {RARITY_LABELS_GEN[pheno.rarity]}
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {pheno.name}
      </span>
      <span style={{ fontSize: 11, color: 'var(--green)', flexShrink: 0 }}>{bonusList}</span>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>Day {pheno.discoveredDay}</span>
    </div>
  );
}

function AppealBar({ label, value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 110 ? 'fill-green' : pct >= 85 ? 'fill-blue' : pct >= 60 ? 'fill-yellow' : 'fill-red';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', width: 70, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 5, background: 'var(--border-light)', borderRadius: 3, overflow: 'hidden' }}>
        <div className={`fill ${color}`} style={{ width: `${Math.min(100, pct / 1.4 * 100)}%`, height: '100%', borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 10, color: 'var(--text)', width: 28, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

export function GeneticsScreen({ state, dispatch }) {
  const unlockedStrains = STRAINS_LIST.filter(s => state.unlockedStrains.includes(s.id));
  const lockedStrains   = STRAINS_LIST.filter(s => !state.unlockedStrains.includes(s.id));

  return (
    <div className="screen">

      {/* Seed inventory summary */}
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-title">Seed Bank</div>
        <motion.div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} variants={staggerContainer} initial="initial" animate="animate">
          {STRAINS_LIST.filter(s => (state.seedInventory[s.id] || 0) > 0).map(s => (
            <motion.div key={s.id} variants={staggerChild} style={{ background: 'var(--border-light)', border: '1px solid var(--green-dim)', borderRadius: 'var(--radius-sm)', padding: '4px 12px', fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              <span style={{ color: 'var(--green)', marginLeft: 8 }}>{state.seedInventory[s.id]} seeds</span>
            </motion.div>
          ))}
          {Object.values(state.seedInventory).every(v => v === 0) && (
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No seeds in inventory.</span>
          )}
        </motion.div>
      </div>

      {/* My Phenos library */}
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-title">🧬 My Phenos</div>
        {state.phenotypes.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            No phenos saved yet. Research Phenohunting Lab (C1) to begin.
          </div>
        ) : (
          STRAINS_LIST
            .filter(s => state.phenotypes.some(p => p.strainId === s.id))
            .map(strain => (
              <div key={strain.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>
                  {strain.name}
                </div>
                {state.phenotypes
                  .filter(p => p.strainId === strain.id)
                  .map(pheno => <PhenoEntry key={pheno.id} pheno={pheno} />)
                }
              </div>
            ))
        )}
      </div>

      {/* Unlocked strains */}
      <div style={{ marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--green)' }}>
          ✅ Unlocked Strains ({unlockedStrains.length})
        </h3>
        <motion.div className="strain-grid" variants={staggerContainer} initial="initial" animate="animate">
          {unlockedStrains.map(strain => (
            <motion.div key={strain.id} variants={staggerChild}>
              <StrainCard
                strain={strain}
                seeds={state.seedInventory[strain.id] || 0}
                cash={state.cash}
                unlocked
                onBuySeeds={(qty) => dispatch({ type: 'BUY_SEEDS', payload: { strainId: strain.id, quantity: qty } })}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Locked strains */}
      {lockedStrains.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--text-muted)' }}>
            🔒 Available to Unlock ({lockedStrains.length})
          </h3>
          <motion.div className="strain-grid" variants={staggerContainer} initial="initial" animate="animate">
            {lockedStrains.map(strain => {
              const licenseBlocked = state.licenseLevel < (strain.minLicense ?? strain.tier);
              const canAfford =
                !licenseBlocked && (
                  (strain.unlockCostType === 'cash' && state.cash >= strain.unlockCost) ||
                  (strain.unlockCostType === 'rp'   && state.researchPoints >= strain.unlockCost) ||
                  strain.unlockCostType === 'free'
                );
              return (
                <motion.div key={strain.id} variants={staggerChild}>
                  <StrainCard
                    strain={strain}
                    seeds={0}
                    unlocked={false}
                    canAfford={canAfford}
                    licenseBlocked={licenseBlocked}
                    requiredLicense={LICENSE_NAMES[strain.minLicense ?? strain.tier]}
                    onUnlock={() => dispatch({ type: 'UNLOCK_STRAIN', payload: { strainId: strain.id } })}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      )}
    </div>
  );
}

const SEED_QTYS = [10, 25, 50];

function StrainCard({ strain, seeds, cash, unlocked, canAfford, licenseBlocked, requiredLicense, onUnlock, onBuySeeds }) {
  const [buyQty, setBuyQty] = useState(10);
  const vegDays   = strain.veg.days;
  const flwrDays  = strain.flower.days;
  const totalDays = vegDays + flwrDays + 3; // +3 seedling

  return (
    <motion.div
      className={`strain-card ${unlocked ? 'unlocked-strain' : 'locked-strain'}`}
      style={{ opacity: licenseBlocked ? 0.45 : 1 }}
      whileHover={licenseBlocked ? {} : { scale: 1.02, y: -2 }}
      whileTap={licenseBlocked ? {} : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      {/* Bud image banner */}
      {(() => {
        const budSrc = process.env.PUBLIC_URL + `/assets/strains/${strain.id}.png`;
        return (
          <div style={{ height: 90, borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 10, background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <img
              src={budSrc}
              alt={strain.name}
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', filter: unlocked ? 'none' : 'opacity(0.65)' }}
            />
            <div style={{ display: 'none', fontSize: 40 }}>🌿</div>
          </div>
        );
      })()}
      <div className="strain-card-header">
        <div>
          <div className="strain-card-name">{strain.name}</div>
          <span className={`strain-card-tier tier-${strain.tier}`}>Tier {strain.tier}</span>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.4 }}>
        {strain.description}
      </p>

      {/* Key stats */}
      <div style={{ marginBottom: 8 }}>
        {[
          { label: 'Yield',      val: `${strain.yieldCeiling}g/plant` },
          { label: 'Max THC',    val: `${strain.thcCeiling}%` },
          { label: 'CBD',        val: `${strain.cbdCeiling}%` },
          { label: 'Veg time',   val: `${vegDays}d` },
          { label: 'Flower time', val: `${flwrDays}d` },
          { label: 'Total cycle', val: `${totalDays}d` },
        ].map(({ label, val }) => (
          <div key={label} className="strain-stat-row">
            <span className="lbl">{label}</span>
            <span className="val">{val}</span>
          </div>
        ))}
      </div>

      {/* Risk indicators */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
        {strain.moldSusceptibility > 0.25   && <span className="alert-pill warning" style={{ fontSize: 10 }}>🍄 Mold risk</span>}
        {strain.hermRisk > 0.15             && <span className="alert-pill high" style={{ fontSize: 10 }}>⚡ Herm risk</span>}
        {strain.pestSusceptibility > 0.25   && <span className="alert-pill warning" style={{ fontSize: 10 }}>🐛 Pest risk</span>}
        {strain.tier === 3                  && <span className="alert-pill info" style={{ fontSize: 10 }}>🔬 Advanced</span>}
      </div>

      {/* Channel appeal */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>Market Appeal</div>
        {Object.entries(strain.channelAppeal).map(([ch, val]) => (
          <AppealBar key={ch} label={`${CHANNEL_LABELS[ch]} ${ch}`} value={val} />
        ))}
      </div>

      {/* Seed count / unlock button */}
      {unlocked ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--green)' }}>{seeds} seeds in stock</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>${strain.marketBasePrice}/g base</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select
              value={buyQty}
              onChange={e => setBuyQty(Number(e.target.value))}
              style={{ fontSize: 11, background: 'var(--border-light)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 'var(--radius-sm)', padding: '2px 4px', flex: 1 }}
            >
              {SEED_QTYS.map(q => (
                <option key={q} value={q}>{q} seeds — ${(strain.seedCost * q).toLocaleString()}</option>
              ))}
            </select>
            <motion.button
              className="btn btn-primary"
              style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }}
              disabled={cash < strain.seedCost * buyQty}
              onClick={() => onBuySeeds(buyQty)}
              whileHover={buttonHover}
              whileTap={buttonTap}
            >
              Buy
            </motion.button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 6 }}>
          {licenseBlocked ? (
            <div style={{ fontSize: 11, color: 'var(--yellow)' }}>
              🪪 Requires {requiredLicense} — purchase in Market tab
            </div>
          ) : (
            <motion.button
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={!canAfford}
              onClick={onUnlock}
              whileHover={buttonHover}
              whileTap={buttonTap}
            >
              Unlock —{' '}
              {strain.unlockCostType === 'free' ? 'Free' :
               strain.unlockCostType === 'cash' ? `$${strain.unlockCost.toLocaleString()}` :
               `${strain.unlockCost} RP`}
            </motion.button>
          )}
        </div>
      )}
    </motion.div>
  );
}
